/**
 * Click-to-dismiss for antd `message` toasts, portal-wide.
 *
 * antd's message toasts have no close affordance and sit at the top-center of
 * the viewport — in the editor that is exactly on top of the Taskbar's aspect
 * ratio picker, so "Aspect set to 9:16" blocks the very control you'd use to
 * change it back. antd exposes an `onClick` per message but the codebase has
 * ~70 static `message.*` callsites plus a handful of `App.useApp().message`
 * instances, so this is done once at the DOM level instead: any click inside a
 * message notice hides that notice immediately. rc-notification's own timer
 * still removes the node later, so React never sees a node we detached.
 *
 * Hides via an inline style (not a class or removal): React reconciles
 * `className` on that element and would resurrect the toast on re-render, and
 * removing the node would make React's later `removeChild` throw.
 */

const NOTICE_SELECTOR = '.ant-message-notice-wrapper, .ant-message-notice';

let uninstall: (() => void) | null = null;

/** Hide the toast that contains `target`, if any. Returns true when one was hidden. */
export function dismissToastAt(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const notice = target.closest<HTMLElement>(NOTICE_SELECTOR);
  if (!notice) return false;
  // Prefer the outer wrapper so the collapsed slot doesn't leave a gap.
  const wrapper = notice.closest<HTMLElement>('.ant-message-notice-wrapper') ?? notice;
  wrapper.style.display = 'none';
  wrapper.setAttribute('aria-hidden', 'true');
  return true;
}

/**
 * Install the document-level listener. Idempotent; returns a function that
 * removes it (handy for tests and HMR).
 */
export function installClickToDismissToasts(doc: Document = document): () => void {
  if (uninstall) return uninstall;
  const onClick = (event: MouseEvent) => {
    dismissToastAt(event.target);
  };
  doc.addEventListener('click', onClick);
  uninstall = () => {
    doc.removeEventListener('click', onClick);
    uninstall = null;
  };
  return uninstall;
}
